import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, Sparkles, Heart, Share2, Lightbulb } from 'lucide-react';

export const InterestSharingBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const features = [
    {
      icon: Sparkles,
      title: 'גלה כישרונות',
      description: 'מצא בקלות אנשים עם כישורים ותחומי מומחיות שמשלימים את הצרכים שלך ושל הקהילה',
    },
    {
      icon: Heart,
      title: 'שתף תשוקות',
      description: 'התחבר לחברי קהילה בעלי תחומי עניין דומים וצור קשרים משמעותיים מבוססי תחביבים ותשוקות משותפות',
    },
    {
      icon: Share2,
      title: 'צמח ביחד',
      description: 'שיתוף ידע ומשאבים מעצים את כולם - למד מאחרים ושתף את הניסיון שלך כדי לחזק את הקהילה כולה',
    },
  ];

  return (
    <section ref={ref} className="relative py-12 sm:py-16 px-6 bg-gradient-to-br from-secondary-100 via-surface to-secondary-100 overflow-hidden">

      {/* Animated background elements */}
      <motion.div
        className="absolute top-20 right-10 w-64 h-64 bg-secondary-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 bg-brand-400/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Title section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="inline-block mb-4"
          >
            <div className="relative">
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-brand-400/10 rounded-full blur-xl"
              />
              <div className="relative bg-gradient-to-br from-secondary-500/20 to-brand-400/20 backdrop-blur-sm p-3 sm:p-3.5 md:p-4 rounded-full">
                <Users className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-brand-600" strokeWidth={1.5} />
              </div>
            </div>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-l from-brand-700 via-brand-600 to-brand-700 leading-tight mb-3">
            חיבור דרך
            <br />
            תחומי עניין משותפים
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-brand-700 font-medium max-w-3xl mx-auto"
          >
            שיתוף ידע ותחומי עניין הוא המפתח לבניית קהילה חזקה ותוססת
          </motion.p>
        </motion.div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.15 }}
              whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
              className="group"
            >
              <div className="bg-white/60 backdrop-blur-md rounded-3xl p-4 sm:p-5 md:p-6 border border-secondary-500/20 h-full hover:bg-white/80 transition-all duration-300 shadow-xl">
                {/* Icon */}
                <div className="mb-6">
                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-secondary-500 rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                    <div className="relative bg-gradient-to-br from-secondary-500/20 to-brand-400/20 backdrop-blur-sm p-3 sm:p-3.5 md:p-4 rounded-2xl group-hover:bg-secondary-500/30 transition-colors">
                      <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-brand-600" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-strong mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-base sm:text-base md:text-lg text-brand-700 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Visual representation of knowledge sharing */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="relative"
        >
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-10 border border-secondary-500/20">
            <div className="flex flex-wrap justify-center items-center gap-8">
              {/* Central lightbulb icon representing ideas/knowledge */}
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="absolute inset-0 bg-white rounded-full blur-2xl opacity-30" />
                <div className="relative bg-gradient-to-br from-white to-white/80 p-5 sm:p-6 md:p-8 rounded-full shadow-2xl">
                  <Lightbulb className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-secondary-500" fill="currentColor" strokeWidth={1.5} />
                </div>
              </motion.div>

              {/* Connecting icons around - representing different interests and skills */}
              <div className="flex flex-wrap justify-center gap-6">
                {[
                  { icon: Sparkles, color: 'from-secondary-500 to-secondary-700' },
                  { icon: Heart, color: 'from-brand-400 to-brand-500' },
                  { icon: Share2, color: 'from-secondary-500 to-secondary-600' },
                  { icon: Users, color: 'from-brand-600 to-brand-700' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={isInView ? {
                      opacity: 1,
                      scale: 1,
                    } : {}}
                    transition={{
                      duration: 0.5,
                      delay: 1 + i * 0.1,
                    }}
                    className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.color} border-2 border-white/40 flex items-center justify-center`}
                  >
                    <item.icon className="w-8 h-8 text-white" strokeWidth={2} />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.5 }}
              className="mt-8 text-center"
            >
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-ink-strong leading-relaxed">
                "כשאנחנו משתפים, כולנו מנצחים"
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
